import React from 'react';

function Privacy() {
  return (
    <div className="privacy">
      <div className="privacy-container">
        <h1 className="section-title">Privacy Policy</h1>
        <p className="section-subtitle">
          Last Updated: January 2025
        </p>

        <div className="privacy-content">
          <p className="privacy-intro">
            At Invex, we are committed to protecting your privacy and ensuring the security of your business data. 
            This Privacy Policy explains how we collect, use, store, and protect information when you use Invex, 
            our comprehensive business management application.
          </p>

          <h3>1. Information We Collect</h3>
          <p>
            When you use Invex, we collect and process the following types of information to provide you with 
            our services:
          </p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, business name, and authentication credentials</li>
            <li><strong>Business Data:</strong> Product inventory, sales transactions, supplier information, customer data, and financial records</li>
            <li><strong>Location Data:</strong> Store locations and business addresses you provide</li>
          </ul>

          <h3>2. How We Use Your Information</h3>
          <p>
            All data collected through Invex is used exclusively to provide, maintain, and improve the features 
            and functionality of our application. Specifically, we use your information to:
          </p>
          <ul>
            <li><strong>Core Functionality:</strong> Enable POS transactions, inventory tracking, supplier management, and sales analytics</li>
            <li><strong>Business Intelligence:</strong> Generate reports, analytics, and insights to help you make informed business decisions</li>
            <li><strong>Data Synchronization:</strong> Ensure your data is accessible across all your devices and locations in real-time</li>
            <li><strong>VAT Compliance:</strong> Calculate taxes, generate ZATCA-compliant QR codes, and maintain regulatory compliance</li>
            <li><strong>Account Management:</strong> Authenticate users, manage permissions, and maintain account security</li>
            <li><strong>Customer Support:</strong> Respond to your inquiries, troubleshoot issues, and provide technical assistance</li>
            <li><strong>Service Improvement:</strong> Analyze usage patterns to enhance features, fix bugs, and optimize performance</li>
            <li><strong>Communication:</strong> Send important updates, security alerts, and service notifications</li>
          </ul>

          <h3>3. Data Storage and Security</h3>
          <p>
            We take the security of your business data seriously and implement industry-standard measures to protect it:
          </p>
          <ul>
            <li><strong>Encryption:</strong> All data transmitted between your device and our servers is encrypted using SSL/TLS protocols</li>
            <li><strong>Cloud Infrastructure:</strong> Your data is stored on secure Firebase cloud servers with automatic backups</li>
            <li><strong>Access Controls:</strong> Role-based permissions ensure only authorized users can access specific data</li>
            <li><strong>Data Isolation:</strong> Multi-tenant architecture with strict data separation ensures your business data 
            remains isolated from other users</li>
            <li><strong>Regular Backups:</strong> Automatic daily backups to prevent data loss</li>
            <li><strong>Monitoring:</strong> Continuous security monitoring to detect and prevent unauthorized access</li>
          </ul>

          <h3>4. Data Sharing and Disclosure</h3>
          <p>
            We respect your business confidentiality. Your data is never sold, rented, or shared with third parties 
            for marketing purposes. We may share information only in the following limited circumstances:
          </p>
          <ul>
            <li><strong>Service Providers:</strong> We use trusted third-party services to operate 
            our application. These providers are bound by strict confidentiality agreements and can only process data 
            according to our instructions</li>
            <li><strong>Business Protection:</strong> To protect our rights, property, or safety, or that of our users or the public</li>
          </ul>

          <h3>5. Data Retention</h3>
          <p>
            We retain your business data for as long as your account remains active or as needed to provide you services. 
            If you choose to delete your account:
          </p>
          <ul>
            <li>You can request complete deletion of your data at any time</li>
            <li>We will delete your data within 30 days of account closure, except where required by law to retain certain records</li>
            <li>Backup copies may persist for up to 90 days before being permanently deleted</li>
            <li>Financial and tax-related data may be retained longer to comply with Saudi Arabian regulatory requirements</li>
          </ul>

          <h3>6. Your Rights and Control</h3>
          <p>
            You have complete control over your data:
          </p>
          <ul>
            <li><strong>Correction:</strong> Update or correct your information directly in the app</li>
            <li><strong>Deletion:</strong> Request deletion of your account and all associated data</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications while continuing to receive essential service updates</li>
          </ul>

          <h3>7. Multi-Store and Multi-User Access</h3>
          <p>
            If you manage multiple store locations or have multiple users:
          </p>
          <ul>
            <li>Each store's data is isolated and accessible only to authorized users for that specific store</li>
            <li>Account owners can control access permissions for each user and location</li>
            <li>Activity logs track all user actions for security and accountability</li>
            <li>You can revoke user access at any time</li>
          </ul>

          <h3>8. Compliance with Saudi Regulations</h3>
          <p>
            Invex is designed to comply with Saudi Arabian regulations, including:
          </p>
          <ul>
            <li>ZATCA (Zakat, Tax and Customs Authority) e-invoicing requirements</li>
            <li>VAT compliance and reporting standards</li>
            <li>Data protection and privacy regulations applicable in the Kingdom of Saudi Arabia</li>
            <li>Financial record-keeping requirements for businesses</li>
          </ul>

          <h3>9. Changes to This Privacy Policy</h3>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, 
            legal requirements, or other factors. When we make significant changes:
          </p>
          <ul>
            <li>We will notify you via email or in-app notification</li>
            <li>The "Last Updated" date at the top of this policy will be revised</li>
            <li>Continued use of Invex after changes constitutes acceptance of the updated policy</li>
            <li>You can always review the current version in the app or on our website</li>
          </ul>

          <h3>10. Contact Us</h3>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle 
            your data, please contact us:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> privacy@invexapp.com</p>
            <p><strong>Support:</strong> support@invexapp.com</p>
            <p><strong>Company:</strong> Invex</p>
            <p><strong>Location:</strong> Saudi Arabia</p>
          </div>

          <h3>14. Your Consent</h3>
          <p>
            By using Invex, you acknowledge that you have read and understood this Privacy Policy and consent 
            to the collection, use, and processing of your information as described herein. If you do not agree 
            with this policy, please do not use our services.
          </p>

          <div className="privacy-summary">
            <h4>In Summary</h4>
            <p>
              Your business data is used solely to provide you with the best possible business management experience 
              through Invex. We implement strong security measures to protect your information, never sell your data 
              to third parties, and give you complete control over your information. We are committed to transparency, 
              security, and compliance with all applicable regulations in Saudi Arabia.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Privacy;